import { useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { useT } from '../lib/i18n';

type Drag = { id: string; from: number; to: number; dy: number; pitch: number };

/**
 * Drag to reorder a vertical list by a handle (mouse, finger or arrow keys). Other items make room
 * while dragging; the new order is reported once, on drop.
 */
export function useSortable(ids: string[], onReorder: (ids: string[]) => void) {
  const refs = useRef(new Map<string, HTMLElement>());
  const [drag, setDrag] = useState<Drag | null>(null);

  function start(e: ReactPointerEvent, id: string) {
    if (e.button !== 0) return;
    e.preventDefault();
    const from = ids.indexOf(id);
    const rects = ids.map((x) => refs.current.get(x)!.getBoundingClientRect());
    const me = rects[from]!;
    const next = rects[from + 1] ?? rects[from - 1];
    // Distance between items (height plus any gap), so the others shift by exactly one slot.
    const pitch = next ? Math.abs(next.top - me.top) : me.height;
    const centers = rects.map((r) => r.top + r.height / 2);
    const y0 = e.clientY;
    let state: Drag = { id, from, to: from, dy: 0, pitch };
    setDrag(state);

    const move = (ev: PointerEvent) => {
      const dy = ev.clientY - y0;
      const center = centers[from]! + dy;
      let to = 0;
      centers.forEach((c, i) => {
        if (i !== from && c < center) to++;
      });
      state = { ...state, dy, to };
      setDrag(state);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      setDrag(null);
      if (state.to !== state.from) onReorder(moved(ids, state.from, state.to));
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }

  function onKey(e: KeyboardEvent, id: string) {
    const from = ids.indexOf(id);
    const to = e.key === 'ArrowUp' ? from - 1 : e.key === 'ArrowDown' ? from + 1 : -1;
    if (to < 0 || to >= ids.length || e.key.length < 2) return;
    e.preventDefault();
    onReorder(moved(ids, from, to));
    // Keep focus on the handle after the list re-renders.
    requestAnimationFrame(() => refs.current.get(id)?.querySelector<HTMLElement>('[data-sort-handle]')?.focus());
  }

  /** Spread onto each item's outer element. */
  const itemProps = (id: string): { ref: (el: HTMLElement | null) => void; style: CSSProperties } => {
    let transform: string | undefined;
    if (drag) {
      const i = ids.indexOf(id);
      if (id === drag.id) transform = `translateY(${drag.dy}px)`;
      else if (drag.from < drag.to && i > drag.from && i <= drag.to) transform = `translateY(${-drag.pitch}px)`;
      else if (drag.from > drag.to && i < drag.from && i >= drag.to) transform = `translateY(${drag.pitch}px)`;
    }
    return {
      ref: (el) => {
        if (el) refs.current.set(id, el);
        else refs.current.delete(id);
      },
      style: {
        transform,
        transition: drag && id !== drag.id ? 'transform 150ms ease' : undefined,
        position: drag?.id === id ? 'relative' : undefined,
        zIndex: drag?.id === id ? 10 : undefined,
        boxShadow: drag?.id === id ? '0 8px 24px rgba(0,0,0,0.12)' : undefined,
      },
    };
  };

  return { itemProps, start, onKey, dragging: drag?.id ?? null };
}

const moved = (ids: string[], from: number, to: number) => {
  const next = [...ids];
  const [x] = next.splice(from, 1);
  next.splice(to, 0, x!);
  return next;
};

/** The grip: drag it, or focus it and use the arrow keys. */
export function SortHandle({ label, onPointerDown, onKeyDown }: { label: string; onPointerDown: (e: ReactPointerEvent) => void; onKeyDown: (e: KeyboardEvent) => void }) {
  const t = useT();
  return (
    <button
      type="button"
      data-sort-handle
      aria-label={`${t('sort.move')}: ${label}`}
      title={t('sort.hint')}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onClick={(e) => e.stopPropagation()}
      className="flex min-h-11 w-8 shrink-0 cursor-grab touch-none items-center justify-center rounded-[var(--radius-control)] text-muted hover:bg-subtle hover:text-ink active:cursor-grabbing"
    >
      <svg aria-hidden width="12" height="18" viewBox="0 0 12 18" fill="currentColor">
        {[3, 9, 15].flatMap((y) => [<circle key={`a${y}`} cx="3" cy={y} r="1.6" />, <circle key={`b${y}`} cx="9" cy={y} r="1.6" />])}
      </svg>
    </button>
  );
}
